import { buildAuthUser } from '../domain/buildAuthUser';
import { hadActivityForPrincipal } from '../domain/hadActivityForPrincipal';
import { isDeactivatedPrincipal } from '../domain/isDeactivatedPrincipal';
import { isUsableBuilderPrincipal } from '../domain/isUsableBuilderPrincipal';
import { ExchangeError } from '../ports/exchange';
import { startRefreshLoop } from './refreshLoop';
function failureToReason(failure) {
    switch (failure.kind) {
        case 'deleted':
            return 'deleted';
        case 'inactive':
            return 'inactive';
        default:
            return 'exchange-failed';
    }
}
function buildEmailUnverifiedUser(identity) {
    return {
        kind: 'email-unverified',
        user: {
            email: identity.email || '',
            profile: {
                displayName: identity.displayName || '',
                photoURL: identity.photoURL || '',
            },
        },
    };
}
export function startAuthEngine(deps) {
    const { firebase, exchange, store } = deps;
    let readyResolve = null;
    const readyPromise = new Promise((resolve) => {
        readyResolve = resolve;
    });
    let isReady = false;
    const commitReady = (status) => {
        if (!isReady) {
            isReady = true;
            readyResolve?.(status);
        }
    };
    const setStatus = (next) => {
        store.setStatus(next);
        if (next.kind !== 'loading')
            commitReady(next);
    };
    let refreshLoop = null;
    const stopRefresh = () => {
        if (refreshLoop) {
            refreshLoop.stop();
            refreshLoop = null;
        }
    };
    let currentIdentity = null;
    const runPipelineFor = async (identity) => {
        // Always exchange first. The backend may update the Firebase user's `emailVerified`
        // during exchange when the principal is already active in another account, so we
        // can no longer short-circuit on Firebase's local copy of `emailVerified`.
        let exchangeResponse;
        try {
            const idToken = await identity.getIdToken(false);
            exchangeResponse = await exchange.exchange(idToken);
        }
        catch (error) {
            stopRefresh();
            if (error instanceof ExchangeError) {
                setStatus({ kind: 'blocked', reason: failureToReason(error.failure) });
            }
            else {
                setStatus({ kind: 'blocked', reason: 'exchange-failed' });
            }
            return;
        }
        // If Firebase still shows unverified but the subject says had_activity, the backend
        // has flipped Firebase's emailVerified server-side — reload to pick up the new value.
        if (!identity.emailVerified && hadActivityForPrincipal(exchangeResponse.subject)) {
            try {
                await identity.reload();
            }
            catch {
                // If reload fails, fall through — we'll just keep the prior value of emailVerified.
            }
        }
        if (!identity.emailVerified) {
            stopRefresh();
            setStatus(buildEmailUnverifiedUser(identity));
            return;
        }
        // Build the AuthUser first so gate rejections can still carry it — the blocked
        // screen renders the rejected user's name + email.
        const user = buildAuthUser(identity, exchangeResponse);
        // Vision gate: only Builder-account principals and Rowi admins get past login;
        // everyone else lands on the blocked screen (graceful, per implementation strategy).
        if (!user.isRowiAdmin && !isUsableBuilderPrincipal(exchangeResponse.subject)) {
            stopRefresh();
            setStatus({ kind: 'blocked', reason: 'unsupported-account', user });
            return;
        }
        if (isDeactivatedPrincipal(exchangeResponse.subject)) {
            stopRefresh();
            setStatus({ kind: 'blocked', reason: 'inactive', user });
            return;
        }
        setStatus({ kind: 'authenticated', user });
        // Start (or restart) the refresh loop.
        if (refreshLoop)
            refreshLoop.stop();
        refreshLoop = startRefreshLoop({
            getIdentityToken: () => identity.getIdToken(false),
            refreshIdentityToken: () => identity.getIdToken(true),
            async onRefreshed() {
                await runPipelineFor(identity);
            },
        });
    };
    const unsubscribe = firebase.onIdentityChanged(async (identity) => {
        currentIdentity = identity;
        if (!identity) {
            stopRefresh();
            setStatus({ kind: 'anonymous' });
            return;
        }
        await runPipelineFor(identity);
    });
    return {
        dispose() {
            unsubscribe();
            stopRefresh();
        },
        async refreshStatus() {
            const identity = firebase.getCurrentIdentity() || currentIdentity;
            if (!identity) {
                setStatus({ kind: 'anonymous' });
                return;
            }
            // Firebase reload() updates the in-memory identity fields (e.g. emailVerified)
            // but does NOT fire onAuthStateChanged, so callers need refreshStatus() to re-evaluate.
            try {
                await identity.reload();
            }
            catch {
                // If reload fails, fall through to re-evaluate with whatever we have.
            }
            await runPipelineFor(identity);
        },
        whenReady() {
            return readyPromise;
        },
    };
}
